import React, { useCallback, useEffect, useMemo, useState, FormEvent } from 'react';
import {
  ProcoreRemoteProject,
  Project,
  ProjectStatus,
  projectsApi,
  syncApi,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { Link, useNavigate } from '../../router/Router';
import { Badge, BadgeVariant, formatRelativeTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconFolder, IconPlus, IconSync, IconTrash } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';
import { usePaginatedList } from '../../hooks/usePagination';

export function projectStatusBadge(status: ProjectStatus): {
  variant: BadgeVariant;
  label: string;
} {
  switch (status) {
    case 'connected':
      return { variant: 'success', label: 'Connected' };
    case 'sync_failed':
      return { variant: 'danger', label: 'Sync Failed' };
    case 'disabled':
      return { variant: 'warning', label: 'Disabled' };
    default:
      return { variant: 'neutral', label: 'Not Configured' };
  }
}

export function ProjectsPage() {
  const { company, user } = useAuth();
  const { pushToast } = useToast();
  const navigate = useNavigate();

  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [syncingProjectId, setSyncingProjectId] = useState<string | null>(null);

  // Add Project Wizard State
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [remoteProcoreProjects, setRemoteProcoreProjects] = useState<ProcoreRemoteProject[]>([]);
  const [connectedProcoreIds, setConnectedProcoreIds] = useState<number[]>([]);
  const [loadingRemote, setLoadingRemote] = useState<boolean>(false);
  const [remoteSearchQuery, setRemoteSearchQuery] = useState<string>('');
  const [selectedRemoteId, setSelectedRemoteId] = useState<string>('');
  const [nameInput, setNameInput] = useState<string>('');
  const [procoreProjectIdInput, setProcoreProjectIdInput] = useState<string>('');
  const [creatingProject, setCreatingProject] = useState<boolean>(false);

  // Delete Confirmation Dialog State
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const list = await projectsApi.listProjects(company?.id || user?.company_id || undefined);
      setProjects(Array.isArray(list) ? list : []);
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Unable to load projects',
        description: err instanceof Error ? err.message : 'Check your authentication token.',
      });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [company?.id, user?.company_id, pushToast]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const getRemoteProjectId = (
    rp: ProcoreRemoteProject & { procore_project_id?: number },
  ): number => Number(rp.id ?? rp.procore_project_id ?? 0);

  const openAddProjectModal = async () => {
    setAddOpen(true);
    setRemoteSearchQuery('');
    setSelectedRemoteId('');
    setNameInput('');
    setProcoreProjectIdInput('');
    setLoadingRemote(true);
    try {
      const [remoteList, connectedIds] = await Promise.all([
        syncApi.listProcoreProjects(company?.procore_company_id).catch(() => []),
        projectsApi.getConnectedProcoreProjectIds(company?.id).catch(() => []),
      ]);
      const validRemote = (Array.isArray(remoteList) ? remoteList : []).map((rp) => ({
        ...rp,
        id: getRemoteProjectId(rp),
      }));
      const validConnected = Array.isArray(connectedIds) ? connectedIds : [];
      setRemoteProcoreProjects(validRemote);
      setConnectedProcoreIds(validConnected);
      const firstAvailable = validRemote.find(
        (rp) => rp.id > 0 && !validConnected.includes(rp.id),
      );
      if (firstAvailable) {
        setSelectedRemoteId(String(firstAvailable.id));
        setNameInput(firstAvailable.name);
        setProcoreProjectIdInput(String(firstAvailable.id));
      }
    } finally {
      setLoadingRemote(false);
    }
  };

  const handleCreateProject = async (e: FormEvent) => {
    e.preventDefault();
    const selectedRemote = remoteProcoreProjects.find(
      (r) => String(getRemoteProjectId(r)) === selectedRemoteId,
    );
    const resolvedName = selectedRemote ? selectedRemote.name.trim() : nameInput.trim();
    const resolvedProcoreId = selectedRemote
      ? getRemoteProjectId(selectedRemote)
      : Number(procoreProjectIdInput);

    if (!resolvedName || !Number.isFinite(resolvedProcoreId) || resolvedProcoreId <= 0) {
      pushToast({
        tone: 'warning',
        title: 'Select a Procore project',
        description: 'Choose a Procore project from the list to connect.',
      });
      return;
    }

    const procoreCompanyId = Number(
      selectedRemote?.company_id || company?.procore_company_id || 1001,
    );

    setCreatingProject(true);
    try {
      const created = await projectsApi.createProject({
        name: resolvedName,
        procore_project_id: resolvedProcoreId,
        procore_company_id: procoreCompanyId,
        billing_user_id: user?.id,
      });
      setProjects((prev) => [created, ...prev]);
      setAddOpen(false);
      setSelectedRemoteId('');
      setNameInput('');
      setProcoreProjectIdInput('');
      pushToast({
        tone: 'success',
        title: 'Project connected',
        description: `${created.name} added. Configure your first sync pipe now.`,
      });
      navigate(`/projects/${created.id}`);
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not create project',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setCreatingProject(false);
    }
  };

  const handleSyncProjectAllPipes = async (project: Project) => {
    setSyncingProjectId(project.id);
    try {
      const res = await syncApi.syncProjectPipes(project.id, 'procore_to_smartsheet');
      pushToast({
        tone: 'success',
        title: `Queued sync for ${project.name}`,
        description: `Job ${res.job_id} dispatched (${res.direction}).`,
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Sync trigger failed',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSyncingProjectId(null);
    }
  };

  const handleConfirmDeleteProject = async () => {
    if (!projectToDelete) return;
    setDeleting(true);
    try {
      await projectsApi.deleteProject(projectToDelete.id);
      setProjects((prev) => prev.filter((p) => p.id !== projectToDelete.id));
      pushToast({
        tone: 'info',
        title: 'Project removed',
        description: `${projectToDelete.name} and its synchronization pipes were deleted.`,
      });
      setProjectToDelete(null);
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to delete project',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setDeleting(false);
    }
  };

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        String(p.procore_project_id).includes(q) ||
        p.id.toLowerCase().includes(q)
      );
    });
  }, [projects, searchQuery, statusFilter]);

  const projectsPagination = usePaginatedList(filteredProjects, 10);

  const filteredRemoteProcoreProjects = useMemo(() => {
    const q = remoteSearchQuery.trim().toLowerCase();
    if (!q) return remoteProcoreProjects;
    return remoteProcoreProjects.filter((rp) => {
      const rec = rp as ProcoreRemoteProject & {
        city?: string | null;
        state_code?: string | null;
      };
      return (
        rp.name.toLowerCase().includes(q) ||
        (rp.project_number && rp.project_number.toLowerCase().includes(q)) ||
        String(rp.id).includes(q) ||
        (rp.stage && rp.stage.toLowerCase().includes(q)) ||
        (rec.city && rec.city.toLowerCase().includes(q)) ||
        (rec.state_code && rec.state_code.toLowerCase().includes(q))
      );
    });
  }, [remoteProcoreProjects, remoteSearchQuery]);

  const remoteProjectsPagination = usePaginatedList(filteredRemoteProcoreProjects, 10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Connected Construction Projects
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Manage Procore construction projects and configure automated data synchronization pipes
            between Procore and Smartsheet.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => loadProjects()}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <IconSync className="w-4 h-4" />
            <span>Refresh</span>
          </button>

          <button
            type="button"
            onClick={openAddProjectModal}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white shadow-xs hover:bg-indigo-700 transition-colors"
          >
            <IconPlus className="w-4 h-4" />
            <span>Add Procore Project</span>
          </button>
        </div>
      </div>

      {/* Summary KPI Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium text-slate-500">Total Projects</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 tabular-nums">{projects.length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium text-slate-500">Connected &amp; Active</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600 tabular-nums">
            {projects.filter((p) => p.status === 'connected').length}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium text-slate-500">Webhooks Active</p>
          <p className="mt-1 text-2xl font-bold text-indigo-600 tabular-nums">
            {projects.filter((p) => p.webhook?.status === 'active').length}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-medium text-slate-500">Needs Attention</p>
          <p className="mt-1 text-2xl font-bold text-rose-600 tabular-nums">
            {projects.filter((p) => p.status === 'sync_failed').length}
          </p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex-1">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              projectsPagination.setPage(0);
            }}
            placeholder="Filter projects by name, Procore Project ID, or UUID…"
            className="w-full rounded-xl border border-slate-300 px-3.5 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="project-status-filter" className="text-xs font-medium text-slate-600">
            Status:
          </label>
          <select
            id="project-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              projectsPagination.setPage(0);
            }}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700"
          >
            <option value="all">All Statuses</option>
            <option value="connected">Connected</option>
            <option value="not_configured">Not Configured</option>
            <option value="sync_failed">Sync Failed</option>
            <option value="disabled">Disabled</option>
          </select>
        </div>
      </div>

      {/* Projects Table / List */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Loading connected Procore projects…
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
              <IconFolder className="w-6 h-6" />
            </div>
            <h3 className="text-base font-semibold text-slate-900">No projects found</h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Connect your first Procore construction project to start synchronizing schedules and
              tasks with Smartsheet.
            </p>
            <button
              type="button"
              onClick={openAddProjectModal}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
            >
              <IconPlus className="w-4 h-4" />
              <span>Add Your First Project</span>
            </button>
          </div>
        ) : (
          <>
            <div className="table-scroll-container">
              <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="px-5 py-3.5">Project Name</th>
                    <th className="px-4 py-3.5">Procore IDs</th>
                    <th className="px-4 py-3.5">Sync Status</th>
                    <th className="px-4 py-3.5">Procore Webhook</th>
                    <th className="px-4 py-3.5">Schedule Calendar</th>
                    <th className="px-4 py-3.5">Updated</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {projectsPagination.pageItems.map((project) => {
                    const statusBadge = projectStatusBadge(project.status);
                    const webhookActive = project.webhook?.status === 'active';

                    return (
                      <tr key={project.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-5 py-4">
                          <Link
                            to={`/projects/${project.id}`}
                            className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            {project.name}
                          </Link>
                          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500 tabular-nums">
                            <span className="font-mono">{project.id.slice(0, 8)}…</span>
                            {project.is_separate_invoice && (
                              <span className="rounded bg-amber-50 px-1.5 py-0.2 text-[10px] font-medium text-amber-800 border border-amber-200">
                                Separate Invoice
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-4 font-mono text-slate-700 tabular-nums">
                          <div>Proj #{project.procore_project_id}</div>
                          <div className="text-[11px] text-slate-400">
                            Comp #{project.procore_company_id}
                          </div>
                        </td>

                        <td className="px-4 py-4">
                          <Badge variant={statusBadge.variant} dot>
                            {statusBadge.label}
                          </Badge>
                        </td>

                        <td className="px-4 py-4 tabular-nums">
                          {webhookActive ? (
                            <Badge variant="success" dot>
                              Active ({project.webhook.triggers?.length || 0} triggers)
                            </Badge>
                          ) : (
                            <Badge variant="neutral">Not Installed</Badge>
                          )}
                        </td>

                        <td className="px-4 py-4 text-slate-600 tabular-nums">
                          <div>{project.settings?.working_days?.length || 5} working days/wk</div>
                          <div className="text-[11px] text-slate-400">
                            {project.settings?.notification_policy?.replace(/_/g, ' ') ||
                              'send once per day'}
                          </div>
                        </td>

                        <td className="px-4 py-4 text-slate-600 tabular-nums">
                          {formatRelativeTime(project.updated_at)}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <div className="inline-flex items-center justify-end gap-2">
                            <Link
                              to={`/projects/${project.id}`}
                              className="rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                            >
                              Configure Pipes →
                            </Link>
                            <button
                              type="button"
                              disabled={syncingProjectId === project.id}
                              onClick={() => handleSyncProjectAllPipes(project)}
                              className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                              title="Queue sync for all project pipes"
                            >
                              <IconSync className="w-3.5 h-3.5" />
                              <span>{syncingProjectId === project.id ? 'Queueing…' : 'Sync'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setProjectToDelete(project)}
                              className="rounded-lg border border-rose-200 bg-rose-50 p-1.5 text-rose-700 hover:bg-rose-100"
                              title="Delete project"
                            >
                              <IconTrash className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar pagination={projectsPagination} itemLabel="projects" />
          </>
        )}
      </div>

      {/* Add Project Wizard Dialog */}
      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Connect a Procore Project"
        subtitle="Select a project from your connected Procore company to add it to Sync."
        maxWidthClass="max-w-xl"
      >
        <form onSubmit={handleCreateProject} className="space-y-4">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <label
                htmlFor="procore-project-search"
                className="block text-xs font-semibold text-slate-700"
              >
                Procore Project
              </label>
              {remoteProcoreProjects.length > 0 && (
                <span className="text-[11px] text-slate-500 tabular-nums">
                  {
                    remoteProcoreProjects.filter((rp) => !connectedProcoreIds.includes(rp.id))
                      .length
                  }{' '}
                  available of {remoteProcoreProjects.length} total
                </span>
              )}
            </div>

            {loadingRemote ? (
              <p className="text-xs text-slate-500 py-3">Fetching projects from Procore…</p>
            ) : remoteProcoreProjects.length > 0 ? (
              <div className="space-y-2.5">
                <input
                  id="procore-project-search"
                  type="search"
                  value={remoteSearchQuery}
                  onChange={(e) => {
                    setRemoteSearchQuery(e.target.value);
                    remoteProjectsPagination.setPage(0);
                  }}
                  placeholder="Search Procore projects by name, project #, ID, or location…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs"
                />

                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                  {remoteProjectsPagination.pageItems.length === 0 ? (
                    <div className="p-6 text-center text-xs text-slate-500">
                      No Procore projects match &ldquo;{remoteSearchQuery}&rdquo;.
                    </div>
                  ) : (
                    <ul className="divide-y divide-slate-200 max-h-72 overflow-y-auto">
                      {remoteProjectsPagination.pageItems.map((rp) => {
                        const alreadyConnected = connectedProcoreIds.includes(rp.id);
                        const isSelected = selectedRemoteId === String(rp.id);
                        const rec = rp as ProcoreRemoteProject & {
                          city?: string | null;
                          state_code?: string | null;
                        };
                        const locationLabel = [rec.city, rec.state_code]
                          .filter(Boolean)
                          .join(', ');

                        return (
                          <li key={rp.id}>
                            <button
                              type="button"
                              disabled={alreadyConnected}
                              onClick={() => {
                                setSelectedRemoteId(String(rp.id));
                                setNameInput(rp.name);
                                setProcoreProjectIdInput(String(rp.id));
                              }}
                              className={`w-full text-left px-3.5 py-2.5 flex items-center justify-between gap-3 transition-colors ${
                                alreadyConnected
                                  ? 'bg-slate-50/70 opacity-60 cursor-not-allowed'
                                  : isSelected
                                    ? 'bg-indigo-50/90'
                                    : 'hover:bg-slate-50 cursor-pointer'
                              }`}
                            >
                              <div className="flex items-start gap-2.5 min-w-0">
                                <input
                                  type="radio"
                                  name="procore-remote-project"
                                  checked={isSelected}
                                  disabled={alreadyConnected}
                                  onChange={() => {
                                    setSelectedRemoteId(String(rp.id));
                                    setNameInput(rp.name);
                                    setProcoreProjectIdInput(String(rp.id));
                                  }}
                                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                                />
                                <div className="min-w-0">
                                  <div className="text-xs font-semibold text-slate-900 truncate">
                                    {rp.name}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500 tabular-nums">
                                    <span className="font-mono">ID #{rp.id}</span>
                                    {rp.project_number && (
                                      <span>· Proj #{rp.project_number}</span>
                                    )}
                                    {locationLabel && <span>· {locationLabel}</span>}
                                    {rp.stage && <span>· {rp.stage}</span>}
                                  </div>
                                </div>
                              </div>

                              <div className="shrink-0">
                                {alreadyConnected ? (
                                  <Badge variant="neutral">Already Connected</Badge>
                                ) : isSelected ? (
                                  <Badge variant="info">Selected</Badge>
                                ) : null}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <PaginationBar
                    pagination={remoteProjectsPagination}
                    itemLabel="Procore projects"
                    compact
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 space-y-2">
                  <p>No active Procore projects were found for this company.</p>
                  <p>
                    Make sure your Procore account has access to projects,{' '}
                    <Link
                      to="/auth/procore-company"
                      onClick={() => setAddOpen(false)}
                      className="font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      switch Procore company
                    </Link>
                    , or enter the project details manually below.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="manual-project-name"
                      className="block text-xs font-medium text-slate-700 mb-1"
                    >
                      Project Name
                    </label>
                    <input
                      id="manual-project-name"
                      type="text"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      placeholder="Downtown Medical Center"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="manual-procore-project-id"
                      className="block text-xs font-medium text-slate-700 mb-1"
                    >
                      Procore Project ID
                    </label>
                    <input
                      id="manual-procore-project-id"
                      type="number"
                      value={procoreProjectIdInput}
                      onChange={(e) => setProcoreProjectIdInput(e.target.value)}
                      placeholder="500101"
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-xs font-mono tabular-nums"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                creatingProject ||
                (!selectedRemoteId && (!nameInput.trim() || !procoreProjectIdInput.trim()))
              }
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingProject ? 'Connecting Project…' : 'Connect Project'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* Delete Project Confirmation Dialog */}
      <Dialog
        open={Boolean(projectToDelete)}
        onClose={() => setProjectToDelete(null)}
        title="Delete Connected Project?"
        subtitle="This action removes the project and all of its configured synchronization pipes."
      >
        {projectToDelete && (
          <div className="space-y-4">
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-900">
              Are you sure you want to delete <strong>{projectToDelete.name}</strong> (Procore
              Project #{projectToDelete.procore_project_id})? Any active webhooks and column
              mappings for this project will be removed.
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setProjectToDelete(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={handleConfirmDeleteProject}
                className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete Project'}
              </button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}
